import moment from "moment-timezone";
import {
  isCompleted,
  isScheduled,
  isStudentCanceled,
  isStudentMissed,
} from "services/sessions/status";
import type {
  LearningPlanMemberWithSessions,
  LearningPlanProgressSession,
  TransformableLearningPlan,
  TransformedLearningPlanFields,
} from "./models";

type ScheduledLearningPlanSession = Pick<LearningPlanProgressSession, "scheduled">;

export function getNumberOfCompletedLearningPlanSessions<
  TSession extends LearningPlanProgressSession,
>(member: LearningPlanMemberWithSessions<TSession>) {
  return member.sessions.filter(session => isCompleted(session.status)).length;
}

export function getNumberOfSkippedLearningPlanSessions<
  TSession extends LearningPlanProgressSession,
>(member: LearningPlanMemberWithSessions<TSession>) {
  return member.sessions.filter(
    session => isStudentMissed(session.status) || isStudentCanceled(session.status)
  ).length;
}

export function getLearningPlanProgressBarSessions<
  TSession extends LearningPlanProgressSession,
>(member: LearningPlanMemberWithSessions<TSession>) {
  return member.sessions.filter(
    session =>
      isCompleted(session.status) ||
      isStudentMissed(session.status) ||
      isStudentCanceled(session.status) ||
      isScheduled(session.status)
  );
}

export function getLastLearningPlanSession<TSession extends LearningPlanProgressSession>(
  member: LearningPlanMemberWithSessions<TSession>
) {
  return member.sessions.reduce<TSession | null>((lastSession, session) => {
    if (!isCompleted(session.status)) {
      return lastSession;
    }

    if (!lastSession || moment.utc(session.scheduled).isAfter(lastSession.scheduled)) {
      return session;
    }

    return lastSession;
  }, null);
}

export function getLearningPlanTargetDate(
  learningPlan: Pick<TransformableLearningPlan, "duration" | "weekly_frequency">,
  startDate: string
) {
  const duration = Number(learningPlan.duration);
  const weeklyFrequency = Number(learningPlan.weekly_frequency);
  const numberOfWeeks = Math.ceil(duration / weeklyFrequency);

  return moment.utc(startDate).add(numberOfWeeks, "weeks").toISOString();
}

export function getLearningPlanFrequencyString(weeklyFrequency?: number | string | null) {
  if (!weeklyFrequency) {
    return "";
  }

  const parsedWeeklyFrequency = parseInt(String(weeklyFrequency), 10);
  if (parsedWeeklyFrequency < 1) {
    return "";
  }
  if (parsedWeeklyFrequency === 1) {
    return "Once a week";
  }
  if (parsedWeeklyFrequency === 2) {
    return "Twice a week";
  }

  return parsedWeeklyFrequency + " times a week";
}

export function transformLearningPlan<
  TSession extends ScheduledLearningPlanSession,
  TLearningPlan extends TransformableLearningPlan<TSession>,
>(learningPlan: TLearningPlan): TLearningPlan & TransformedLearningPlanFields<TSession> {
  const numberOfSessions = parseInt(String(learningPlan.duration), 10);
  const sessions = [...(learningPlan.sessions ?? [])].sort(
    (left, right) =>
      moment.utc(left.scheduled).toDate().getTime() -
      moment.utc(right.scheduled).toDate().getTime()
  );
  const lastSession = sessions[numberOfSessions - 1] ?? null;

  return {
    ...learningPlan,
    from: learningPlan.start_date,
    number_of_sessions: numberOfSessions,
    sessions,
    to: lastSession
      ? lastSession.scheduled
      : getLearningPlanTargetDate(learningPlan, learningPlan.start_date),
    frequencyString: getLearningPlanFrequencyString(learningPlan.weekly_frequency),
  };
}
